import React, { useState, useEffect } from "react";
import IngredientRow from "./IngredientRow";

export default function RecipeCard({
  recipe,
  recipeIdx,
  ingredientDetails,
  total,
  onRefresh,
  refreshing,
  refreshProgress,
  lastUpdated,
  onDelete,
  onAddToShoppingList,
  setReplaceModal,
  setRecipes,
  updateRecipeInAirtable,
  isNew
}) {
  const [expanded, setExpanded] = useState(false);
  const [computedTotal, setComputedTotal] = useState(total);

  useEffect(() => {
    const newTotal = ingredientDetails.reduce(
      (sum, item) => sum + ((parseFloat(item.price.replace('£', '')) || 0) * (item.quantity || 1)),
      0
    );
    setComputedTotal(newTotal);
  }, [ingredientDetails]);

  return (
    <div className={`p-4 sm:p-4 border-2 border-gray-400 rounded-xl shadow-lg ${isNew ? 'bg-blue-100' : 'bg-white'}`}>
      <h2
        className="mb-2"
        style={{
          borderBottom: '1px solid #ccc',
          paddingBottom: '12px',
          fontWeight: 'bold',
          fontSize: '24px'
        }}
      >
        {recipe.title}
      </h2>
      <div className="mb-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <span className="font-bold text-base sm:text-lg">Total: £{computedTotal.toFixed(2)}</span>
        <div className="flex flex-col items-end sm:items-start sm:flex-row sm:items-center gap-2">
          <div className="flex flex-col items-end sm:items-start">
            <button
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs mb-1"
              disabled={refreshing}
              onClick={onRefresh}
            >
              {refreshing && refreshProgress ? `Updating... (${refreshProgress.current} of ${refreshProgress.total})` : refreshing ? 'Updating...' : 'Refresh Prices'}
            </button>
            <div className="text-xs text-gray-500 text-right sm:text-left">
              Last updated: {lastUpdated ? (typeof lastUpdated === 'string' ? new Date(lastUpdated).toLocaleDateString() : lastUpdated.toLocaleDateString()) : 'Never'}
            </div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mb-2">
          <div className="bg-gray-300 rounded-lg p-2">
            <div className="w-full font-semibold border-b pb-1 text-left text-xs sm:text-base hidden sm:grid sm:grid-cols-[2.5rem_1fr_4.5rem_5.5rem_5.5rem] sm:gap-0 gap-1" style={{ paddingRight: '16px' }}>
              <div></div>
              <div>Ingredient</div>
              <div>Qty</div>
              <div>Price</div>
              <div>Actions</div>
            </div>
            <div className="max-h-60 overflow-x-auto bg-white rounded-md" style={{ scrollbarGutter: 'stable' }}>
              {ingredientDetails.map((item, i) => (
                <IngredientRow
                  key={i}
                  item={item}
                  i={i}
                  recipeIdx={recipeIdx}
                  draggable
                  onDragStart={e => {
                    e.dataTransfer.setData('application/json', JSON.stringify({ ingredient: item }));
                  }}
                  onDelete={async () => {
                    setRecipes((prev) => {
                      const updated = prev.map((rec, ridx) =>
                        ridx === recipeIdx
                          ? { ...rec, ingredients: rec.ingredients.filter((_, ii) => ii !== i) }
                          : rec
                      );
                      // Sync to Airtable
                      const updatedRecipe = updated[recipeIdx];
                      if (updatedRecipe) updateRecipeInAirtable(updatedRecipe);
                      return updated;
                    });
                  }}
                  onReplace={() => setReplaceModal({ recipeIdx, ingredientIdx: i })}
                  rowBg={i % 2 === 0 ? 'bg-white' : 'bg-gray-100'}
                />
              ))}
            </div>
          </div>
          {/* Add new ingredient button row and drop target */}
          <div
            className="flex items-center border-b bg-green-100 rounded-md mb-2"
            style={{ marginTop: '10px', padding: '16px' }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              const data = e.dataTransfer.getData('application/json');
              if (data) {
                const { ingredient } = JSON.parse(data);
                setRecipes(prev => prev.map((rec, ridx) => {
                  if (ridx === recipeIdx) {
                    // Avoid duplicates
                    if (rec.ingredients.some(ing => ing.title === ingredient.title && ing.link === ingredient.link && ing.productId === ingredient.productId)) return rec;
                    const updated = { ...rec, ingredients: [...rec.ingredients, ingredient] };
                    updateRecipeInAirtable(updated);
                    return updated;
                  }
                  return rec;
                }));
              }
            }}
          >
            <span className="flex-1 text-left text-green-900 font-medium pl-2">drag &amp; drop ingredient items here</span>
            <button
              className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded hover:bg-blue-700 text-xs ml-2"
              onClick={() => setReplaceModal({ recipeIdx, ingredientIdx: null })}
            >
              + Add Ingredient
            </button>
          </div>
        </div>
      )}
      <div className="relative flex flex-col sm:flex-row gap-2 mt-2 min-h-[48px]">
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <button
            onClick={onAddToShoppingList}
            className="bg-green-600 text-white px-4 py-3 rounded hover:bg-green-700 w-full sm:w-auto text-base"
          >
            Add to Shopping List
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this recipe?')) {
                onDelete();
              }
            }}
            className="bg-red-600 text-white px-4 py-3 rounded hover:bg-red-700 w-full sm:w-auto text-base"
          >
            Delete
          </button>
        </div>
        <button
          className="mt-2 sm:mt-0 px-3 py-2 rounded text-xs border border-gray-400 hover:bg-gray-100 w-full sm:w-auto sm:absolute sm:right-0 sm:bottom-0"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      </div>
    </div>
  );
}
